
import CryptoJS from 'crypto-js';

// Static source strings provided by user
const KEY_SOURCE = "24c036ba0ac6e8c79beba8b24d27d41a";
const IV_SOURCE = "de3048e85e07fe23b1acd6c808987195";

let _key = null;
let _iv = null;

const getKeyIv = () => {
    if (_key && _iv) return { key: _key, iv: _iv };

    // Derive Key and IV using MD5
    _key = CryptoJS.MD5(KEY_SOURCE);
    _iv = CryptoJS.MD5(IV_SOURCE);

    return { key: _key, iv: _iv };
};

// Helper: WordArray to Uint8Array
const wordToByteArray = (wordArray) => {
    const words = wordArray.words;
    const sigBytes = wordArray.sigBytes;
    const bytes = new Uint8Array(sigBytes);
    for (let i = 0; i < sigBytes; i++) {
        bytes[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
    }
    return bytes;
};

// Helper: Uint8Array to WordArray
const byteToWordArray = (uint8Array) => {
    const words = [];
    for (let i = 0; i < uint8Array.length; i++) {
        words[i >>> 2] |= (uint8Array[i] & 0xff) << (24 - (i % 4) * 8);
    }
    return CryptoJS.lib.WordArray.create(words, uint8Array.length);
};

export const encryptBookingId = (bookingId) => {
    if (!bookingId) return "";

    try {
        const { key, iv } = getKeyIv();
        const dataStr = String(bookingId);

        // Convert input to bytes
        const dataUtf8 = CryptoJS.enc.Utf8.parse(dataStr);
        const dataBytes = wordToByteArray(dataUtf8);

        // Prepare Key/IV
        // Note: IV is mutable in CFB, so we clone it conceptually by converting to byte array and modifying that array
        const keyBytes = key;
        const ivBytes = wordToByteArray(iv);

        if (ivBytes.length !== 16) {
            console.error("IV size mismatch");
            return "";
        }

        const encryptedBytes = new Uint8Array(dataBytes.length);
        const aes = CryptoJS.algo.AES.createEncryptor(keyBytes);

        for (let i = 0; i < dataBytes.length; i++) {
            // Encrypt current IV (shift register)
            const ivBlock = byteToWordArray(ivBytes);

            // Allow access to internal words array for encryptBlock
            const M = ivBlock.words.slice();
            aes.encryptBlock(M, 0);

            // Get first byte of output (KeyStream byte)
            const ksByte = (M[0] >>> 24) & 0xff;

            const cipherByte = dataBytes[i] ^ ksByte;
            encryptedBytes[i] = cipherByte;

            // Shift IV left 8 bits, append ciphertext byte
            for (let j = 0; j < 15; j++) {
                ivBytes[j] = ivBytes[j + 1];
            }
            ivBytes[15] = cipherByte;
        }

        // Base64 encode
        const encryptedWordArray = byteToWordArray(encryptedBytes);
        const base64 = CryptoJS.enc.Base64.stringify(encryptedWordArray);

        // URL Safe
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (error) {
        console.error("Encryption error:", error);
        return "";
    }
};

export const decryptBookingId = (encryptedId) => {
    if (!encryptedId) return "";

    try {
        let base64 = encryptedId.replace(/-/g, '+').replace(/_/g, '/');
        while (base64.length % 4) base64 += '=';

        const { key, iv } = getKeyIv();

        const encryptedWordArray = CryptoJS.enc.Base64.parse(base64);
        const encryptedBytes = wordToByteArray(encryptedWordArray);

        const keyBytes = key;
        const ivBytes = wordToByteArray(iv);

        const decryptedBytes = new Uint8Array(encryptedBytes.length);
        const aes = CryptoJS.algo.AES.createEncryptor(keyBytes); // Decrypt uses Encryptor in CFB

        for (let i = 0; i < encryptedBytes.length; i++) {
            const ivBlock = byteToWordArray(ivBytes);
            const M = ivBlock.words.slice();
            aes.encryptBlock(M, 0);

            const ksByte = (M[0] >>> 24) & 0xff;
            const cipherByte = encryptedBytes[i];

            const plainByte = cipherByte ^ ksByte;
            decryptedBytes[i] = plainByte;

            // Shift IV left 8 bits, append CIPHERTEXT byte
            for (let j = 0; j < 15; j++) {
                ivBytes[j] = ivBytes[j + 1];
            }
            ivBytes[15] = cipherByte;
        }

        const decryptedWordArray = byteToWordArray(decryptedBytes);
        const decryptedStr = CryptoJS.enc.Utf8.stringify(decryptedWordArray);
        return decryptedStr || encryptedId;
    } catch (error) {
        // Return original encrypted token so it can be decrypted by backend
        return encryptedId;
    }
};
